import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import {
    getProduct
} from "../services/ProductService";

import ProductVariantService
    from "../services/ProductVariantService";

import ProductShippingService
    from "../services/ProductShippingService";

import pb
    from "../../lib/pb";

import {
    useNavigate,
    useParams
} from "react-router-dom";

import {
    useCart
} from "../context/CartContext";


export default function BrandMarketplaceProduct({
    brand
}) {

    const {
        productId
    } = useParams();

    const navigate =
        useNavigate();

    const {
        addToCart
    } = useCart();


    /*
    --------------------------------------------------
    STATE
    --------------------------------------------------
    */

    const [product, setProduct] =
        useState(null);

    const [variants, setVariants] =
        useState([]);

    const [shipping, setShipping] =
        useState(null);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState(null);

    const [activeImage, setActiveImage] =
        useState(0);

    const [quantity, setQuantity] =
        useState(1);

    const [selectedVariant, setSelectedVariant] =
        useState(null);


    /*
    --------------------------------------------------
    LOAD PRODUCT
    --------------------------------------------------
    */

    useEffect(() => {

        if (!productId) {

            setError(
                "Product ID is missing."
            );

            setLoading(false);

            return;

        }

        let active = true;


        const loadProduct = async () => {

            try {

                setLoading(true);

                setError(null);


                const record =
                    await getProduct(
                        productId
                    );


                if (!active) {

                    return;

                }


                /*
                ------------------------------------------
                Marketplace visibility protection
                ------------------------------------------
                */

                if (
                    record.status !==
                    "published"
                ) {

                    setError(
                        "This product is not currently available."
                    );

                    setProduct(null);

                    return;

                }


                if (
                    record.visibility !==
                    "public"
                ) {

                    setError(
                        "This product is not currently available."
                    );

                    setProduct(null);

                    return;

                }


                /*
                ------------------------------------------
                Brand protection
                ------------------------------------------
                */

                if (
                    brand?.id &&
                    record.brand !== brand.id
                ) {

                    setError(
                        "This product does not belong to this brand."
                    );

                    setProduct(null);

                    return;

                }


                setProduct(record);


                /*
                ------------------------------------------
                Load variants
                ------------------------------------------
                */

                try {

                    const variantRecords =
                        await ProductVariantService
                            .listByProduct(
                                record.id
                            );


                    if (active) {

                        setVariants(
                            variantRecords
                        );

                    }

                }

                catch (variantError) {

                    console.error(
                        "Failed to load product variants:",
                        variantError
                    );


                    if (active) {

                        setVariants([]);

                    }

                }


                /*
                ------------------------------------------
                Load shipping
                ------------------------------------------
                */

                try {

                    const shippingRecord =
                        await ProductShippingService
                            .getByProduct(
                                record.id
                            );


                    if (active) {

                        setShipping(
                            shippingRecord
                        );

                    }

                }

                catch (shippingError) {

                    console.error(
                        "Failed to load product shipping:",
                        shippingError
                    );


                    if (active) {

                        setShipping(null);

                    }

                }

            }

            catch (err) {

                console.error(
                    "Failed to load marketplace product:",
                    err
                );


                if (active) {

                    setError(
                        err?.message ||
                        "Unable to load this product."
                    );

                    setProduct(null);

                }

            }

            finally {

                if (active) {

                    setLoading(false);

                }

            }

        };


        loadProduct();


        return () => {

            active = false;

        };

    }, [
        productId,
        brand?.id
    ]);


    /*
    --------------------------------------------------
    PRODUCT IMAGES
    --------------------------------------------------
    */

    const images =
        useMemo(() => {

            if (
                !product ||
                !product.images
            ) {

                return [];

            }


            return Array.isArray(
                product.images
            )

                ? product.images

                : [
                    product.images
                ];

        }, [
            product
        ]);


    /*
    --------------------------------------------------
    POCKETBASE IMAGE URL
    --------------------------------------------------
    */

    const getProductImage = (
        filename
    ) => {

        if (
            !product ||
            !filename
        ) {

            return null;

        }


        try {

            return pb
                .files
                .getURL(
                    product,
                    filename
                );

        }

        catch (err) {

            console.error(
                "Failed to generate image URL:",
                err
            );

            return null;

        }

    };


    /*
    --------------------------------------------------
    CURRENCY
    --------------------------------------------------
    */

    const formatPrice = (
        price
    ) => {

        if (
            price === null ||
            price === undefined ||
            price === ""
        ) {

            return "—";

        }


        try {

            return new Intl.NumberFormat(
                undefined,
                {
                    style: "currency",
                    currency:
                        product?.currency ||
                        "USD"
                }
            ).format(
                Number(price)
            );

        }

        catch {

            return `${product?.currency || ""} ${price}`;

        }

    };


    /*
    --------------------------------------------------
    INVENTORY
    --------------------------------------------------
    */

    const stock =
        Number(
            selectedVariant?.stock ??
            product?.stock ??
            0
        );


    const trackInventory =
        selectedVariant
            ? true
            : product?.trackInventory !== false;


    const allowBackorder =
        selectedVariant
            ? false
            : product?.allowBackorder === true;


    const isInStock =
        !trackInventory ||
        stock > 0 ||
        allowBackorder;


    const maxQuantity =
        trackInventory &&
        stock > 0

            ? stock

            : 999;


    /*
    --------------------------------------------------
    QUANTITY
    --------------------------------------------------
    */

    const increaseQuantity = () => {

        setQuantity(
            previous =>
                Math.min(
                    previous + 1,
                    maxQuantity
                )
        );

    };


    const decreaseQuantity = () => {

        setQuantity(
            previous =>
                Math.max(
                    previous - 1,
                    1
                )
        );

    };


    /*
    --------------------------------------------------
    SELECT VARIANT
    --------------------------------------------------
    */

    const handleVariantSelect = (
        variant
    ) => {

        setSelectedVariant(
            variant
        );

        setQuantity(1);

    };


    /*
    --------------------------------------------------
    ADD TO CART
    --------------------------------------------------
    */

    const handleAddToCart = () => {

        try {

            addToCart(
                product,
                quantity,
                selectedVariant
            );


            navigate(
                "/brand/cart"
            );

        }

        catch (error) {

            console.error(
                "Failed to add product to cart:",
                error
            );


            alert(
                error?.message ||
                "Unable to add product to cart."
            );

        }

    };


    /*
    --------------------------------------------------
    BACK
    --------------------------------------------------
    */

    const handleBack = () => {

        navigate(
            "/brand/marketplace"
        );

    };


    /*
    --------------------------------------------------
    LOADING
    --------------------------------------------------
    */

    if (loading) {

        return (

            <div
                style={{
                    padding: 60,
                    textAlign: "center",
                    color: "#666"
                }}
            >
                Loading product...
            </div>

        );

    }


    /*
    --------------------------------------------------
    ERROR
    --------------------------------------------------
    */

    if (
        error ||
        !product
    ) {

        return (

            <div
                style={{
                    padding: 40
                }}
            >

                <button
                    onClick={handleBack}
                    style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: 0,
                        color: "#2563eb",
                        fontWeight: 600
                    }}
                >
                    ← Back to Marketplace
                </button>


                <div
                    style={{
                        marginTop: 25,
                        background: "#fef2f2",
                        border:
                            "1px solid #fecaca",
                        color: "#991b1b",
                        padding: 25,
                        borderRadius: 14
                    }}
                >
                    {error ||
                        "Product not found."}
                </div>

            </div>

        );

    }


    /*
    --------------------------------------------------
    RENDER
    --------------------------------------------------
    */

    return (

        <div
            style={{
                minHeight: "100%",
                paddingBottom: 50
            }}
        >

            {/* ==================================================
                BACK
            ================================================== */}

            <button
                onClick={handleBack}
                style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: 0,
                    marginBottom: 25,
                    color: "#2563eb",
                    fontWeight: 600,
                    fontSize: 14
                }}
            >
                ← Back to Marketplace
            </button>


            {/* ==================================================
                PRODUCT
            ================================================== */}

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        "minmax(0,1.05fr) minmax(0,.95fr)",
                    gap: 45,
                    background: "#fff",
                    borderRadius: 20,
                    padding: 30,
                    boxShadow:
                        "0 8px 30px rgba(0,0,0,.06)"
                }}
            >

                {/* ==================================================
                    GALLERY
                    ================================================== */}

                <div>

                    <div
                        style={{
                            position: "relative",
                            width: "100%",
                            aspectRatio: "1 / 1",
                            background: "#f1f5f9",
                            borderRadius: 16,
                            overflow: "hidden"
                        }}
                    >

                        {product.featured && (

                            <div
                                style={{
                                    position: "absolute",
                                    top: 15,
                                    left: 15,
                                    zIndex: 2,
                                    background:
                                        "rgb(255,0,204)",
                                    color: "#fff",
                                    padding:
                                        "7px 12px",
                                    borderRadius: 20,
                                    fontSize: 12,
                                    fontWeight: 700
                                }}
                            >
                                Featured
                            </div>

                        )}


                        {images.length > 0 &&
                        getProductImage(
                            images[activeImage]
                        ) ? (

                            <img
                                src={
                                    getProductImage(
                                        images[activeImage]
                                    )
                                }
                                alt={
                                    product.title ||
                                    "Product"
                                }
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain"
                                }}
                            />

                        ) : (

                            <div
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#94a3b8"
                                }}
                            >
                                No image available
                            </div>

                        )}

                    </div>


                    {/* Thumbnails */}

                    {images.length > 1 && (

                        <div
                            style={{
                                display: "flex",
                                gap: 10,
                                marginTop: 15,
                                overflowX: "auto"
                            }}
                        >

                            {images.map(
                                (
                                    filename,
                                    index
                                ) => {

                                    const url =
                                        getProductImage(
                                            filename
                                        );


                                    return (

                                        <button
                                            key={
                                                `${filename}-${index}`
                                            }
                                            onClick={() =>
                                                setActiveImage(
                                                    index
                                                )
                                            }
                                            style={{
                                                width: 75,
                                                height: 75,
                                                padding: 0,
                                                border:
                                                    index ===
                                                    activeImage

                                                        ? "3px solid #2563eb"

                                                        : "1px solid #ddd",
                                                borderRadius: 10,
                                                overflow: "hidden",
                                                background: "#f8fafc",
                                                cursor: "pointer",
                                                flexShrink: 0
                                            }}
                                        >

                                            {url && (

                                                <img
                                                    src={url}
                                                    alt=""
                                                    style={{
                                                        width: "100%",
                                                        height: "100%",
                                                        objectFit: "cover"
                                                    }}
                                                />

                                            )}

                                        </button>

                                    );

                                }
                            )}

                        </div>

                    )}

                </div>


                {/* ==================================================
                    PRODUCT INFORMATION
                    ================================================== */}

                <div>

                    {/* Category */}

                    <div
                        style={{
                            color: "#64748b",
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 10
                        }}
                    >
                        {product.category ||
                            "Product"}
                    </div>


                    {/* Title */}

                    <h1
                        style={{
                            margin: 0,
                            fontSize: 38,
                            lineHeight: 1.15
                        }}
                    >
                        {product.title}
                    </h1>


                    {/* Rating */}

                    <div
                        style={{
                            marginTop: 15,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            color: "#666"
                        }}
                    >

                        <span
                            style={{
                                fontSize: 18,
                                color: "#f59e0b"
                            }}
                        >
                            ★
                        </span>

                        <strong>
                            {Number(
                                product.rating || 0
                            ).toFixed(1)}
                        </strong>

                        <span>
                            (
                            {Number(
                                product.reviewCount ||
                                0
                            )}
                            reviews)
                        </span>

                    </div>


                    {/* Price */}

                    <div
                        style={{
                            marginTop: 25,
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap"
                        }}
                    >

                        <strong
                            style={{
                                fontSize: 32
                            }}
                        >
                            {formatPrice(
                                selectedVariant?.price ??
                                product.price
                            )}
                        </strong>


                        {Number(
                            product.comparePrice ||
                            0
                        ) > 0 && (

                            <span
                                style={{
                                    color: "#999",
                                    textDecoration:
                                        "line-through",
                                    fontSize: 16
                                }}
                            >
                                {formatPrice(
                                    product.comparePrice
                                )}
                            </span>

                        )}

                    </div>


                    {/* Description */}

                    <div
                        style={{
                            marginTop: 25,
                            color: "#4b5563",
                            lineHeight: 1.75
                        }}
                    >

                        {product.shortDescription && (

                            <p
                                style={{
                                    marginTop: 0,
                                    fontWeight: 600
                                }}
                            >
                                {product.shortDescription}
                            </p>

                        )}


                        {product.description && (

                            <div
                                style={{
                                    whiteSpace:
                                        "pre-wrap"
                                }}
                            >
                                {product.description}
                            </div>

                        )}

                    </div>


                    {/* ==================================================
                        VARIANTS
                        ================================================== */}

                    {variants.length > 0 && (

                        <div
                            style={{
                                marginTop: 30
                            }}
                        >

                            <h3
                                style={{
                                    marginBottom: 12
                                }}
                            >
                                Options
                            </h3>


                            <div
                                style={{
                                    display: "grid",
                                    gap: 10
                                }}
                            >

                                {variants.map(
                                    variant => {

                                        const activeVariant =
                                            selectedVariant?.id ===
                                            variant.id;


                                        return (

                                            <button
                                                key={
                                                    variant.id
                                                }
                                                onClick={() =>
                                                    handleVariantSelect(
                                                        variant
                                                    )
                                                }
                                                style={{
                                                    textAlign: "left",
                                                    padding: 14,
                                                    border:
                                                        activeVariant

                                                            ? "2px solid #2563eb"

                                                            : "1px solid #ddd",
                                                    borderRadius: 10,
                                                    background:
                                                        activeVariant

                                                            ? "#eff6ff"

                                                            : "#fff",
                                                    cursor: "pointer"
                                                }}
                                            >

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: 15
                                                    }}
                                                >

                                                    <strong>
                                                        {
                                                            variant.title ||
                                                            variant.name ||
                                                            "Variant"
                                                        }
                                                    </strong>


                                                    {variant.price !==
                                                        undefined &&
                                                    variant.price !==
                                                        null && (

                                                        <span>
                                                            {formatPrice(
                                                                variant.price
                                                            )}
                                                        </span>

                                                    )}

                                                </div>


                                                {variant.stock !==
                                                    undefined && (

                                                    <div
                                                        style={{
                                                            marginTop: 5,
                                                            fontSize: 13,
                                                            color:
                                                                Number(
                                                                    variant.stock
                                                                ) > 0

                                                                    ? "#166534"

                                                                    : "#991b1b"
                                                        }}
                                                    >
                                                        {Number(
                                                            variant.stock
                                                        ) > 0

                                                            ? `${variant.stock} available`

                                                            : "Out of stock"}
                                                    </div>

                                                )}

                                            </button>

                                        );

                                    }
                                )}

                            </div>

                        </div>

                    )}


                    {/* ==================================================
                        INVENTORY
                        ================================================== */}

                    <div
                        style={{
                            marginTop: 25,
                            padding: 15,
                            borderRadius: 10,
                            background:
                                isInStock

                                    ? "#f0fdf4"

                                    : "#fef2f2",
                            color:
                                isInStock

                                    ? "#166534"

                                    : "#991b1b"
                        }}
                    >

                        {isInStock

                            ? trackInventory

                                ? allowBackorder &&
                                  stock <= 0

                                    ? "Available for backorder"

                                    : `${stock} in stock`

                                : "In stock"

                            : "Out of stock"}

                    </div>


                    {/* ==================================================
                        SHIPPING
                        ================================================== */}

                    {shipping && (

                        <div
                            style={{
                                marginTop: 20,
                                padding: 15,
                                borderRadius: 10,
                                background: "#f8fafc",
                                color: "#475569"
                            }}
                        >

                            <strong>
                                Shipping
                            </strong>

                            <div
                                style={{
                                    marginTop: 6,
                                    lineHeight: 1.6
                                }}
                            >
                                {shipping.name ||
                                    shipping.title ||
                                    shipping.description ||
                                    "Shipping information available."}
                            </div>

                        </div>

                    )}


                    {/* ==================================================
                        QUANTITY + CART
                        ================================================== */}

                    {isInStock && (

                        <div
                            style={{
                                marginTop: 20,
                                display: "flex",
                                gap: 12,
                                alignItems: "center",
                                flexWrap: "wrap"
                            }}
                        >

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    border:
                                        "1px solid #ddd",
                                    borderRadius: 10,
                                    overflow: "hidden"
                                }}
                            >

                                <button
                                    onClick={
                                        decreaseQuantity
                                    }
                                    style={{
                                        width: 42,
                                        height: 42,
                                        border: "none",
                                        background: "#fff",
                                        cursor: "pointer",
                                        fontSize: 18
                                    }}
                                >
                                    −
                                </button>


                                <span
                                    style={{
                                        minWidth: 35,
                                        textAlign: "center",
                                        fontWeight: 600
                                    }}
                                >
                                    {quantity}
                                </span>


                                <button
                                    onClick={
                                        increaseQuantity
                                    }
                                    style={{
                                        width: 42,
                                        height: 42,
                                        border: "none",
                                        background: "#fff",
                                        cursor: "pointer",
                                        fontSize: 18
                                    }}
                                >
                                    +
                                </button>

                            </div>


                            <button
                                onClick={
                                    handleAddToCart
                                }
                                style={{
                                    flex: 1,
                                    minWidth: 180,
                                    padding:
                                        "13px 22px",
                                    border: "none",
                                    borderRadius: 10,
                                    background:
                                        "rgb(255,0,204)",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontWeight: 700,
                                    fontSize: 15
                                }}
                            >
                                Add to Cart
                            </button>

                        </div>

                    )}


                    {/* ==================================================
                        PRODUCT META
                        ================================================== */}

                    <div
                        style={{
                            marginTop: 25,
                            display: "grid",
                            gap: 8,
                            fontSize: 13,
                            color: "#64748b"
                        }}
                    >

                        {product.sku && (

                            <div>
                                SKU: {product.sku}
                            </div>

                        )}

                        {product.tags && (

                            <div>
                                Tags:{" "}
                                {Array.isArray(
                                    product.tags
                                )

                                    ? product.tags.join(
                                        ", "
                                    )

                                    : product.tags}
                            </div>

                        )}

                    </div>

                </div>

            </div>

        </div>

    );

}